import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import type {
  IUpdateReviewParams,
  IUpdateReviewRequest,
  IReviewsResponse,
  IReviewComplainParams,
  IReviewComplainRequest,
  IReviewsState,
} from "./reviews.types";
import { Request } from "../../../request/Request";
import type { RootState } from "../../../store";
import { getGlobalHeaders } from "../../browser/browserSlice";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

export const updateReview = createAsyncThunk(
  "global/reviews/updateReview",
  async (params: IUpdateReviewParams, { getState }) => {
    const response = new Request.Foundation<
      IUpdateReviewRequest,
      IReviewsResponse
    >({
      url: `/api/reviews/${params.reviewId}`,
      provider: {
        id: "UPDATE_REVIEW",
      },
      request: {
        method: "PUT",
        data: {
          text: params.text,
        },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const addReviewComplain = createAsyncThunk(
  "global/reviews/addReviewComplain",
  async (params: IReviewComplainParams, { getState }) => {
    const { text, reason } = params;
    const response = new Request.Foundation<
      IReviewComplainRequest,
      IReviewsResponse
    >({
      url: `/api/reviews/${params.reviewId}/complain`,
      provider: {
        id: "ADD_REVIEW_COMPLAIN",
      },
      request: {
        method: "PUT",
        data: { reason, text },
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

const addReviewComplainCase = (
  builder: ActionReducerMapBuilder<IReviewsState>
): void => {
  // Загрузка
  builder.addCase(addReviewComplain.pending, (state: IReviewsState) => {
    state.actions.addReviewComplain.isLoading = true;
    state.actions.addReviewComplain.error = undefined;
  });
  // Ответ
  builder.addCase(
    addReviewComplain.fulfilled,
    (
      state: IReviewsState,
      action: PayloadAction<
        IReduxRequestNode<IReviewComplainRequest, IReviewsResponse>
      >
    ) => {
      state.actions.addReviewComplain.isLoading = false;

      if (action.payload.error) {
        console.error("Ошибка запроса addReviewComplain", action.payload.error);
        state.actions.addReviewComplain.error = action.payload.error;
        return;
      }
    }
  );
};

const initialState: IReviewsState = {
  actions: {
    addReviewComplain: {},
  },
  activeReply: -1,
};

export const reviewsSlice = createSlice({
  name: "global/reviews",
  initialState,
  reducers: {},
  extraReducers: builder => {
    addHydrateReducer(builder, "global.reviews");
    addReviewComplainCase(builder);
  },
});

export default reviewsSlice.reducer;
